import { emptySplitApi as api } from './baseApi';
import type { Attachment, EntityIdentity, Review } from './vmsApi';

export type ProcurementContract = {
  id?: string;
  title: string;
  supplier: EntityIdentity;
  attachments?: Attachment[];
  active: boolean;
  totalValue?: number;
};

export type NewProcurementContract = Omit<ProcurementContract, 'id'>;
export type ProcurementContractPartial = Partial<ProcurementContract>;

export type ContractItem = {
  id?: string;
  contractId: string;
  contractName: string;
  supplierName: string;
  supplierId?: string;
  itemCode: string;
  isService: boolean;
  isVAT?: boolean;
  itemDescription: string;
  itemUnit: string;
  itemBudgetedCost: number;
  itemQuantity: number;
  itemCurrentValue: number;
  initialQuantity: number;
  itemInitialValue: number;
  procuredQuantity?: number;
  procuredValue?: number;
  user?: object;
  isApproved?: boolean;
  firstApproval?: Review;
  secondApproval?: Review;
  [key: string]: any;
};

export type NewContractItem = Omit<ContractItem, 'id'>;

export type ContractItemAuditTrail = {
  id?: string;
  contractItemId: string;
  oldContractItem?: object;
  newContractItem: object;
  auditTrigger?: string;
  firstApproval?: Review;
  secondApproval?: Review;
  user?: object;
  attachments?: Attachment[];
  createdAt?: string;
  updatedAt?: string;
  [key: string]: any;
};

export const addTagTypes = ['ProcurementContract', 'ContractItem', 'ContractItemAuditTrail'] as const;

export type VirtualWarehouseSummary = {
  contractValue: number;
  contractQty: number;
  orderedValue: number;
  orderedQty: number;
  deliveredValue: number;
  deliveredQty: number;
  undeliveredValue: number;
  undeliveredQty: number;
  contractValueLeft: number;
  contractQtyLeft: number;
  issuedValue: number;
  issuedQty: number;
  unit: string;
  purchaseOrders: {
    purchaseOrderNumber: string;
    date?: string;
    status?: string;
    paymentStatus?: string;
    currency?: string;
    supplier?: string;
    orderTotal: number;
    ordered: number;
    delivered: number;
    undelivered: number;
  }[];
  paymentRequisitions: {
    id: string;
    referenceNumber?: string;
    purchaseOrderNumber?: string;
    paymentType?: string;
    status?: string;
    currency?: string;
    amount: number;
    dateCreated?: string;
    dueDate?: string;
  }[];
  grvs: VirtualWarehouseTransfer[];
  givs: VirtualWarehouseTransfer[];
};

export type VirtualWarehouseTransfer = {
  id?: string;
  number?: string;
  purchaseOrderNumber?: string;
  transferredDate?: string;
  currency?: string;
  itemCount: number;
  quantity: number;
  value: number;
};

const procurementContractsApi = api
  .enhanceEndpoints({ addTagTypes })
  .injectEndpoints({
    overrideExisting: true,
    endpoints: (build) => ({
      getProcurementContracts: build.query<ProcurementContract[], void>({
        query: () => ({ url: '/procurement-contracts' }),
        providesTags: ['ProcurementContract'],
      }),

      getProcurementContractById: build.query<ProcurementContract, { id: string }>({
        query: ({ id }) => ({ url: `/procurement-contracts/${id}` }),
        providesTags: ['ProcurementContract'],
      }),

      createProcurementContract: build.mutation<ProcurementContract, { newProcurementContract: NewProcurementContract }>({
        query: ({ newProcurementContract }) => ({
          url: '/procurement-contracts',
          method: 'POST',
          body: newProcurementContract,
        }),
        invalidatesTags: ['ProcurementContract'],
      }),

      updateProcurementContractById: build.mutation<void, { id: string; procurementContractPartial: ProcurementContractPartial }>({
        query: ({ id, procurementContractPartial }) => ({
          url: `/procurement-contracts/${id}`,
          method: 'PATCH',
          body: procurementContractPartial,
        }),
        invalidatesTags: ['ProcurementContract'],
      }),

      deleteProcurementContractById: build.mutation<void, { id: string }>({
        query: ({ id }) => ({
          url: `/procurement-contracts/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['ProcurementContract'],
      }),

      getAllContractItems: build.query<ContractItem[], void>({
        query: () => ({ url: '/contract-items' }),
        providesTags: ['ContractItem'],
      }),

      getVirtualWarehouseSummary: build.query<
        VirtualWarehouseSummary,
        { contractId?: string; contractItemId?: string; fromDate?: string; toDate?: string; currency?: string }
      >({
        query: (params) => ({ url: '/virtual-warehouse/summary', params }),
        providesTags: ['ContractItem', 'ProcurementContract'],
      }),

      getContractItemsByContract: build.query<
        ContractItem[],
        {
          contractId: string;
          itemCode?: string;
          itemDescription?: string;
          itemUnit?: string;
          itemBudgetedCost?: number;
          itemQuantity?: number;
          limit?: number;
          skip?: number;
        }
      >({
        query: ({ contractId, ...params }) => ({
          url: `/contract-items/by-contract/${contractId}`,
          params,
        }),
        providesTags: ['ContractItem'],
      }),

      createContractItem: build.mutation<ContractItem, { newContractItem: NewContractItem }>({
        query: ({ newContractItem }) => ({
          url: '/contract-items',
          method: 'POST',
          body: newContractItem,
        }),
        invalidatesTags: ['ContractItem', 'ProcurementContract'],
      }),

      updateContractItemById: build.mutation<void, { id: string; body: any }>({
        query: ({ id, body }) => ({
          url: `/contract-items/${id}`,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: ['ContractItem', 'ProcurementContract'],
      }),

      deleteContractItemById: build.mutation<void, { id: string }>({
        query: ({ id }) => ({
          url: `/contract-items/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['ContractItem', 'ProcurementContract'],
      }),

      contractItemFirstApproval: build.mutation<void, { id: string; body: { comment?: string } }>({
        query: ({ id, body }) => ({
          url: `/contract-items/${id}/first-approval`,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: ['ContractItem'],
      }),

      contractItemSecondApproval: build.mutation<void, { id: string; body: { comment?: string } }>({
        query: ({ id, body }) => ({
          url: `/contract-items/${id}/second-approval`,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: ['ContractItem'],
      }),

      getContractItemAuditTrailsByContractItem: build.query<
        ContractItemAuditTrail[],
        { contractItemId: string; limit?: number; skip?: number }
      >({
        query: ({ contractItemId, ...params }) => ({
          url: `/contract-item-audit-trails/by-contract-item/${contractItemId}`,
          params,
        }),
        providesTags: ['ContractItemAuditTrail'],
      }),

      contractItemAuditTrailFirstApproval: build.mutation<void, { id: string; body: { comment?: string } }>({
        query: ({ id, body }) => ({
          url: `/contract-item-audit-trails/${id}/first-approval`,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: ['ContractItemAuditTrail'],
      }),

      contractItemAuditTrailSecondApproval: build.mutation<void, { id: string; body: { comment?: string } }>({
        query: ({ id, body }) => ({
          url: `/contract-item-audit-trails/${id}/second-approval`,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: ['ContractItemAuditTrail'],
      }),
    }),
  });

export { procurementContractsApi };

export const {
  useGetProcurementContractsQuery,
  useGetProcurementContractByIdQuery,
  useCreateProcurementContractMutation,
  useUpdateProcurementContractByIdMutation,
  useDeleteProcurementContractByIdMutation,
  useGetAllContractItemsQuery,
  useGetVirtualWarehouseSummaryQuery,
  useGetContractItemsByContractQuery,
  useLazyGetContractItemsByContractQuery,
  useCreateContractItemMutation,
  useUpdateContractItemByIdMutation,
  useDeleteContractItemByIdMutation,
  useContractItemFirstApprovalMutation,
  useContractItemSecondApprovalMutation,
  useGetContractItemAuditTrailsByContractItemQuery,
  useContractItemAuditTrailFirstApprovalMutation,
  useContractItemAuditTrailSecondApprovalMutation,
} = procurementContractsApi;
